import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Fetchexecutequery,
  Fetchupdatequery,
} from "../../../services/process/BRS1/brsService7";

import { headers, baseUrl } from "../../../services/common/credentials";
import { CreateData } from "../../../services/process/process_data/process_data";
import {
  Building, Users, Globe, UserPlus, Check, Save, Upload,
  Link, Plus, Minus, AlertCircle, CheckCircle, ChevronDown, ChevronUp,
  BookOpen, Image, FileText, Video, Eye, GraduationCap, Award,
} from "lucide-react";

// ── Helpers ────────────────────────────────────────────────────────────────────
const getUserCredentials = () =>
  JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

const getFormattedDate = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");

const FONT = "'Inter', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  navy:        "#0a2352",
  navyDark:    "#061535",
  navyMid:     "#143272",
  orange:      "#f97316",
  orangeHover: "#ea6c0a",
  bg:          "#eef3f9",
  card:        "#ffffff",
  border:      "#d4dce8",
  text:        "#0f172a",
  muted:       "#64748b",
  light:       "#e8eef5",
  success:     "#16a34a",
  danger:      "#dc2626",
  warn:        "#d97706",
};

// ── Constants ──────────────────────────────────────────────────────────────────
const STATIC_PATH   = "/home/demo/UserManagementModule/static/reference_files/";
const UPLOAD_URL    = `${baseUrl}/api/v2/user_modules/upload/`;
const DOWNLOAD_BASE = `${baseUrl}/api/v2/user_modules/serve-pdf/`;

const UNIVERSITY_TYPES = [
  "Public University",
  "Private University",
  "Deemed University",
  "Autonomous Institution",
  "Other",
];

const COUNTRIES = [
  "India", "United States", "United Kingdom", "Australia", "Canada",
  "Germany", "France", "Singapore", "UAE", "Netherlands",
  "New Zealand", "Ireland", "Japan", "South Korea", "Other",
];

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka",
  "Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram",
  "Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
  "Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

const PROGRAMS = [
  "Engineering",
  "Business & Management",
  "Computer Science & AI",
  "Data Science",
  "Health Sciences",
  "Hospitality & Tourism",
  "Design & Architecture",
  "Arts & Humanities",
  "Other",
];

// Upload config
const LOGO_CONFIG = {
  key: "logo", label: "University Logo",
  hint: "PNG or JPG with transparent background preferred · Max 5 MB",
  accept: ".png,.jpg,.jpeg", maxMB: 5,
};

// ── Shared UI Atoms ────────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{
    background: T.card, borderRadius: 14,
    border: `1px solid ${T.border}`,
    boxShadow: "0 4px 20px rgba(10,35,82,.07)",
    overflow: "hidden", ...style,
  }}>
    {children}
  </div>
);

const SectionHeader = ({ icon: Icon, title, badge, isOpen, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "14px 20px",
      background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`,
      cursor: "pointer", userSelect: "none",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        background: T.orange, borderRadius: 7, padding: "5px 6px",
        display: "flex", alignItems: "center",
      }}>
        <Icon size={15} color="#fff" />
      </div>
      <span style={{ color: "#fff", fontWeight: 800, fontSize: 14, letterSpacing: .2 }}>{title}</span>
      {badge != null && (
        <span style={{
          background: T.orange, color: "#fff",
          borderRadius: 99, padding: "1px 8px", fontSize: 11, fontWeight: 800,
        }}>{badge}</span>
      )}
    </div>
    {isOpen
      ? <ChevronUp size={18} color="rgba(255,255,255,.75)" />
      : <ChevronDown size={18} color="rgba(255,255,255,.75)" />}
  </div>
);

const Field = ({ label, required, children, hint, fullWidth, style: fs = {} }) => (
  <div style={{ marginBottom: 14, gridColumn: fullWidth ? "1/-1" : undefined, ...fs }}>
    <label style={{
      display: "block", fontSize: 12, fontWeight: 700, color: T.navy,
      marginBottom: 5, letterSpacing: .3, textTransform: "uppercase",
    }}>
      {label}{required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
    </label>
    {children}
    {hint && <p style={{ fontSize: 11, color: T.muted, margin: "4px 0 0" }}>{hint}</p>}
  </div>
);

const inputBase = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: `1.5px solid ${T.border}`, fontSize: 13, color: T.text,
  background: "#fff", outline: "none", boxSizing: "border-box",
  transition: "border .18s, box-shadow .18s", fontFamily: FONT,
};

const Input = ({ style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? T.orange : T.border,
        boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
        ...style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const Select = ({ children, style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? T.orange : T.border,
        boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
        ...style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
};

const Textarea = ({ style = {}, maxLength, value, ...props }) => {
  const [focused, setFocused] = useState(false);
  const wordCount = value ? value.trim().split(/\s+/).filter(Boolean).length : 0;
  return (
    <div>
      <textarea
        value={value}
        {...props}
        style={{
          ...inputBase,
          borderColor: focused ? T.orange : T.border,
          boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none",
          resize: "vertical", minHeight: 100, ...style,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {maxLength && (
        <p style={{
          fontSize: 11, margin: "3px 0 0", textAlign: "right",
          color: wordCount > maxLength ? T.danger : T.muted,
        }}>
          {wordCount} / {maxLength} words
        </p>
      )}
    </div>
  );
};

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const [hovered, setHovered] = useState(false);
  const pad = size === "sm" ? "7px 14px" : "10px 22px";
  const fs = size === "sm" ? 12 : 13;
  const variants = {
    primary:   { background: hovered ? T.orangeHover : T.orange, color: "#fff", border: "none" },
    secondary: { background: hovered ? T.light : "#f1f5f9", color: T.navy, border: `1.5px solid ${T.border}` },
    navy:      { background: hovered ? T.navyMid : T.navy, color: "#fff", border: "none" },
    success:   { background: hovered ? "#15803d" : T.success, color: "#fff", border: "none" },
    outline:   {
      background: "transparent", color: T.navy, border: `1.5px solid ${T.border}`,
      ...(hovered && { borderColor: T.orange, color: T.orange }),
    },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        borderRadius: 8, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
        padding: pad, fontSize: fs, transition: "all .15s",
        opacity: disabled ? .6 : 1, fontFamily: FONT,
        ...variants[variant], ...style,
      }}
    >
      {Icon && <Icon size={size === "sm" ? 13 : 14} />}
      {children}
    </button>
  );
};

const Checkbox = ({ checked, onChange, label, required }) => (
  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 12 }}>
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
        border: `2px solid ${checked ? T.orange : T.border}`,
        background: checked ? T.orange : "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all .15s", cursor: "pointer",
      }}
    >
      {checked && <Check size={11} color="#fff" strokeWidth={3} />}
    </div>
    <span style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>
      {label}{required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
    </span>
  </label>
);

// Radio yes/no
const YesNo = ({ value, onChange }) => (
  <div style={{ display: "flex", gap: 12, marginTop: 2 }}>
    {["Yes", "No"].map(opt => {
      const active = value === opt;
      return (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          style={{
            padding: "7px 22px", borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: `1.5px solid ${active ? T.orange : T.border}`,
            background: active ? "rgba(249,115,22,.1)" : "#fff",
            color: active ? T.orange : T.muted,
            cursor: "pointer", transition: "all .15s", fontFamily: FONT,
          }}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

// Programs multi-toggle
const ProgramToggle = ({ value, onChange }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
    {PROGRAMS.map(p => {
      const active = value.includes(p);
      return (
        <button
          key={p}
          onClick={() => onChange(active ? value.filter(x => x !== p) : [...value, p])}
          style={{
            padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            border: `1.5px solid ${active ? T.orange : T.border}`,
            background: active ? "rgba(249,115,22,.1)" : "#fff",
            color: active ? T.orange : T.muted,
            cursor: "pointer", transition: "all .15s", fontFamily: FONT,
          }}
        >
          {p}
        </button>
      );
    })}
  </div>
);

const DonutChart = ({ pct, size = 88 }) => {
  const r = 36, cx = 44, cy = 44;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 88 88">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={T.light} strokeWidth={14} />
      <circle
        cx={cx} cy={cy} r={r} fill="none"
        stroke={pct > 80 ? T.success : pct > 50 ? T.orange : T.danger}
        strokeWidth={14}
        strokeDasharray={`${filled} ${circ - filled}`}
        style={{ transform: "rotate(-90deg)", transformOrigin: "center", transition: "stroke-dasharray .5s" }}
      />
      <text x="44" y="49" textAnchor="middle" fontSize="15" fontWeight="900" fill={T.navy}>{pct}%</text>
    </svg>
  );
};

const ProgressBar = ({ value, color = T.orange }) => (
  <div style={{ background: T.light, borderRadius: 99, height: 6, overflow: "hidden" }}>
    <div style={{
      width: `${Math.min(value, 100)}%`, height: "100%",
      background: color, borderRadius: 99, transition: "width .5s",
    }} />
  </div>
);

// ── Single File Upload Row ─────────────────────────────────────────────────────
const SingleFileRow = ({ config, uploaded, isUploading, onChoose, onClear }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 16, padding: "12px 16px",
    background: uploaded ? "rgba(249,115,22,.05)" : T.bg,
    borderRadius: 10,
    border: `1.5px dashed ${uploaded ? T.orange : T.border}`,
    fontFamily: FONT,
  }}>
    <div style={{
      background: uploaded ? "rgba(249,115,22,.15)" : T.light,
      borderRadius: 8, padding: 10, flexShrink: 0,
    }}>
      <Upload size={16} color={uploaded ? T.orange : T.navy} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{config.label}</div>
      {isUploading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
          <div style={{
            width: 11, height: 11, border: `2px solid ${T.light}`,
            borderTop: `2px solid ${T.orange}`, borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
          }} />
          <span style={{ fontSize: 12, color: T.orange, fontWeight: 600 }}>Uploading…</span>
        </div>
      ) : uploaded ? (
        <a href={`${DOWNLOAD_BASE}${uploaded.fileName}`} target="_blank" rel="noopener noreferrer"
          style={{
            fontSize: 12, color: "#007bff", textDecoration: "underline",
            display: "block", marginTop: 3, overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
          {uploaded.fileName}
        </a>
      ) : (
        <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{config.hint}</div>
      )}
    </div>
    {!isUploading && (
      uploaded ? (
        <button onClick={onClear} title="Remove" style={{
          cursor: "pointer", background: "none", border: "none",
          color: T.danger, fontWeight: 800, fontSize: 15, lineHeight: 1,
          padding: "2px 7px", flexShrink: 0,
        }}>✕</button>
      ) : (
        <button onClick={onChoose} style={{
          background: "transparent", border: `1.5px solid ${T.border}`,
          borderRadius: 7, padding: "5px 14px", color: T.navy,
          fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center",
          gap: 5, flexShrink: 0, cursor: "pointer", fontFamily: FONT,
        }}>
          <Upload size={12} /> Choose File
        </button>
      )
    )}
  </div>
);

// ── Step indicator ─────────────────────────────────────────────────────────────
const STEPS = [
  { label: "Institution Info", icon: Building       },
  { label: "Contact Details",  icon: Users          },
  { label: "Media & Branding", icon: Image          },
  { label: "Academic Details", icon: BookOpen       },
  { label: "Declaration",      icon: Check          },
];

const StepBar = ({ current }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "18px 32px 0", gap: 0, fontFamily: FONT,
  }}>
    {STEPS.map((s, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <React.Fragment key={i}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 80 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              background: done ? T.success : active ? T.orange : T.light,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: `2px solid ${done ? T.success : active ? T.orange : T.border}`,
              transition: "all .2s",
            }}>
              {done
                ? <Check size={15} color="#fff" strokeWidth={3} />
                : <s.icon size={14} color={active ? "#fff" : T.muted} />}
            </div>
            <span style={{
              fontSize: 10, fontWeight: 700,
              color: active ? T.orange : done ? T.success : T.muted,
              marginTop: 4, textAlign: "center", letterSpacing: .3, textTransform: "uppercase",
            }}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{
              flex: 1, height: 2, background: done ? T.success : T.light,
              margin: "-18px 4px 0", transition: "background .3s",
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Reusable Representatives Table ─────────────────────────────────────────────
const RepsTable = ({ reps, onAdd, onRemove, onUpdate, title = "Representatives" }) => (
  <div>
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      marginBottom: 12,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 800, color: T.navy, textTransform: "uppercase",
        letterSpacing: .6, paddingBottom: 6, borderBottom: `2px solid ${T.light}`,
        flex: 1, marginRight: 16, fontFamily: FONT,
      }}>
        {title} ({reps.length})
      </div>
      <Btn variant="outline" size="sm" icon={UserPlus} onClick={onAdd}>Add Rep</Btn>
    </div>

    {/* Column headers */}
    <div style={{
      display: "grid", gridTemplateColumns: "1fr 1fr 1fr 40px",
      gap: 10, marginBottom: 8, padding: "0 13px", boxSizing: "border-box",
    }}>
      {["Name", "Email", "Mobile", ""].map((h, i) => (
        <div key={i} style={{ fontWeight: 700, fontSize: 12, color: T.navy, fontFamily: FONT }}>{h}</div>
      ))}
    </div>

    {reps.map((rep, i) => (
      <div key={i} style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr 40px",
        gap: 10, marginBottom: 10, padding: 12,
        background: T.bg, borderRadius: 8, border: `1px solid ${T.border}`,
        boxSizing: "border-box",
      }}>
        <Input
          placeholder="Full name"
          value={rep.name}
          onChange={e => onUpdate(i, "name", e.target.value)}
        />
        <Input
          placeholder="Email address"
          type="email"
          value={rep.email}
          onChange={e => onUpdate(i, "email", e.target.value)}
        />
        <Input
          placeholder="+91-XXXXXXXXXX"
          value={rep.phone}
          onChange={e => onUpdate(i, "phone", e.target.value)}
        />
        <button
          onClick={() => onRemove(i)}
          disabled={reps.length === 1}
          style={{
            width: 40, height: "100%",
            background: reps.length === 1 ? T.light : "rgba(220,38,38,.1)",
            border: "none", borderRadius: 8,
            cursor: reps.length === 1 ? "not-allowed" : "pointer",
            color: T.danger,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Minus size={14} />
        </button>
      </div>
    ))}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
function UniversityRegistration() {
  const uc = getUserCredentials();
  const [step, setStep]                       = useState(0);
  const [existingProfile, setExistingProfile] = useState(null);
  const [saving, setSaving]                   = useState(false);
  const [saveStatus, setSaveStatus]           = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [previewMode, setPreviewMode]         = useState(false);

  // ── Form data ──────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    // Institution Info
    university_name: "",
    institution_type: "",
    other_institution_type: "",
    country: "India",
    city: "",
    state: "",
    website_url: "",
    year_established: "",
    institution_overview: "",
    // Primary Contact
    contact_name: "",
    contact_designation: "",
    contact_department: "",
    contact_email: "",
    contact_mobile: "",
    alternate_contact_number: "",
    // Media
    university_logo: "",
    promo_video_url: "",
    // Social
    facebook_page: "",
    instagram_handle: "",
    linkedin_page: "",
    youtube_channel: "",
    // Academic
    programs_featured: [],
    scholarships_discussed: "",
    one_on_one_available: "",
    // Declaration
    consent_accuracy: false,
    consent_media: false,
    authorized_signatory_name: "",
    authorized_signatory_designation: "",
    declaration_date: "",
  });

  // ── Representatives ────────────────────────────────────────────────────────
  const [representatives, setRepresentatives] = useState([{ name: "", email: "", phone: "" }]);
  const addRep    = () => setRepresentatives(p => [...p, { name: "", email: "", phone: "" }]);
  const removeRep = i  => setRepresentatives(p => p.filter((_, idx) => idx !== i));
  const updateRep = (i, key, value) => {
    const r = [...representatives];
    r[i][key] = value;
    setRepresentatives(r);
  };

  // ── File upload ────────────────────────────────────────────────────────────
  const [logoFile, setLogoFile]           = useState(null);
  const [uploadingKeys, setUploadingKeys] = useState({});
  const logoRef = useRef(null);

  // ── Collapsible sections ───────────────────────────────────────────────────
  const [openSections, setOpenSections] = useState({
    basic: true, location: true, overview: true,
    primary: true, reps: true,
    logo: true, video: true, social: true,
    programs: true, participation: true,
    declaration: true,
  });
  const toggleSection = k => setOpenSections(p => ({ ...p, [k]: !p[k] }));

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateStep = (s) => {
    switch (s) {
      case 0: return formData.university_name && formData.institution_type && formData.country && formData.city;
      case 1: return formData.contact_name && formData.contact_designation && formData.contact_department && formData.contact_email && formData.contact_mobile;
      case 2: return !!logoFile;
      case 3: return formData.programs_featured.length > 0;
      case 4: return formData.consent_accuracy && formData.consent_media && formData.authorized_signatory_name && formData.authorized_signatory_designation && formData.declaration_date;
      default: return true;
    }
  };

  // ── Load existing profile ──────────────────────────────────────────────────
  useEffect(() => { fetchExistingProfile(); }, []);

  const fetchExistingProfile = async () => {
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT * FROM edu_platform.university_profile WHERE user_id = ${uc.user_id} AND is_active = 1 LIMIT 1;`,
      }, creds[0]);
      if (res.data && res.data.length > 0) {
        const row = res.data[0];
        setExistingProfile(row.primary_id);
        setFormData(prev => ({
          ...prev,
          university_name:                  row.university_name                  || "",
          institution_type:                 row.institution_type                 || "",
          other_institution_type:           row.other_institution_type           || "",
          country:                          row.country                          || "India",
          city:                             row.city                             || "",
          state:                            row.state                            || "",
          website_url:                      row.website_url                      || "",
          year_established:                 row.year_established                 || "",
          institution_overview:             row.institution_overview             || "",
          contact_name:                     row.contact_name                     || "",
          contact_designation:              row.contact_designation              || "",
          contact_department:               row.contact_department               || "",
          contact_email:                    row.contact_email                    || "",
          contact_mobile:                   row.contact_mobile                   || "",
          alternate_contact_number:         row.alternate_contact_number         || "",
          university_logo:                  row.university_logo                  || "",
          promo_video_url:                  row.promo_video_url                  || "",
          facebook_page:                    row.facebook_page                    || "",
          instagram_handle:                 row.instagram_handle                 || "",
          linkedin_page:                    row.linkedin_page                    || "",
          youtube_channel:                  row.youtube_channel                  || "",
          programs_featured:                row.programs_featured ? JSON.parse(row.programs_featured) : [],
          scholarships_discussed:           row.scholarships_discussed           || "",
          one_on_one_available:             row.one_on_one_available             || "",
          consent_accuracy:                 !!row.consent_accuracy,
          consent_media:                    !!row.consent_media,
          authorized_signatory_name:        row.authorized_signatory_name        || "",
          authorized_signatory_designation: row.authorized_signatory_designation || "",
          declaration_date:                 row.declaration_date                 || "",
        }));
        if (row.university_logo) setLogoFile({ fileName: row.university_logo });
        if (row.representatives) {
          try { setRepresentatives(JSON.parse(row.representatives)); }
          catch { setRepresentatives([{ name: "", email: "", phone: "" }]); }
        }
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) =>
    setFormData(p => ({ ...p, [name]: value }));

  // ── File upload handler ────────────────────────────────────────────────────
  const uploadFile = async (key, maxMB, file) => {
    if (!file) return null;
    if (file.size > maxMB * 1024 * 1024) {
      alert(`File too large. Maximum size is ${maxMB} MB.`);
      return null;
    }
    setUploadingKeys(p => ({ ...p, [key]: true }));
    const fd = new FormData();
    fd.append("file", file);
    fd.append("filePath", STATIC_PATH);
    try {
      const res = await fetch(UPLOAD_URL, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        return { fileName: data.Filename, filePath: data.Filepath };
      } else {
        const err = await res.json();
        alert("Upload failed: " + (err.Message || "Unknown error"));
        return null;
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed. Please try again.");
      return null;
    } finally {
      setUploadingKeys(p => ({ ...p, [key]: false }));
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const result = await uploadFile("logo", LOGO_CONFIG.maxMB, file);
    if (result) { setLogoFile(result); handleChange("university_logo", result.fileName); }
    if (logoRef.current) logoRef.current.value = "";
  };

  // ── Completion ─────────────────────────────────────────────────────────────
  const requiredFields = [
    "university_name", "institution_type", "country", "city",
    "contact_name", "contact_designation", "contact_department",
    "contact_email", "contact_mobile", "institution_overview",
  ];
  const filled = requiredFields.filter(f => formData[f] && String(formData[f]).trim() !== "").length;
  const completion = Math.round((filled / requiredFields.length) * 100);
  const consentDone = formData.consent_accuracy && formData.consent_media &&
    formData.authorized_signatory_name && formData.authorized_signatory_designation && formData.declaration_date;
  const missingRequired = requiredFields.filter(f => !formData[f] || String(formData[f]).trim() === "");

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async (status) => {
    setSaving(true);
    setSaveStatus(null);
    const now = getFormattedDate();
    const payload_data = {
      ...formData,
      programs_featured: JSON.stringify(formData.programs_featured),
      representatives:   JSON.stringify(representatives),
      profile_status:    status,
      modified_date:     now,
      modified_by:       uc.user_id,
      user_id:           uc.user_id,
      is_active:         1,
    };
    try {
      if (existingProfile) {
        const creds = Fetchupdatequery();
        await axios.post(creds[1], {
          schema_name: "edu_platform",
          table: "university_profile",
          set_column: [payload_data],
          where: [{ primary_id: existingProfile }],
        }, creds[0]);
      } else {
        const creds = CreateData();
        await axios.post(creds[1], {
          database: "edu_platform",
          table: "university_profile",
          formData: {
            ...payload_data,
            tenant_id:    uc.tenants_id,
            entity_id:    uc.selectedentity || uc.entities_id,
            group_id:     uc.groups_id,
            created_by:   uc.user_id,
            created_date: now,
          },
        }, creds[0]);
        await fetchExistingProfile();
      }
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 3500);
    } catch (err) {
      console.error("Save error:", err);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: T.navy }}>
          <div style={{
            width: 44, height: 44, border: `4px solid ${T.light}`,
            borderTop: `4px solid ${T.orange}`, borderRadius: "50%",
            animation: "spin 0.8s linear infinite", margin: "0 auto 12px",
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ fontWeight: 700, fontSize: 14, margin: 0, fontFamily: FONT }}>Loading…</p>
        </div>
      </div>
    );
  }

  // ── Step 0: Institution Information ───────────────────────────────────────
  const renderStep0 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      {/* Basic Details */}
      <Card>
        <SectionHeader icon={GraduationCap} title="Basic Details" isOpen={openSections.basic} onToggle={() => toggleSection("basic")} />
        {openSections.basic && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "repeat(3, minmax(240px,1fr))", gap: 16 }}>
            <Field label="Name of the University / Institution" required fullWidth>
              <Input
                value={formData.university_name}
                onChange={e => handleChange("university_name", e.target.value)}
                placeholder="Enter full institution name"
              />
            </Field>
            <Field label="Type of Institution" required>
              <Select value={formData.institution_type} onChange={e => handleChange("institution_type", e.target.value)}>
                <option value="">Select type</option>
                {UNIVERSITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </Field>
            {formData.institution_type === "Other" && (
              <Field label="Specify Type" required>
                <Input
                  value={formData.other_institution_type}
                  onChange={e => handleChange("other_institution_type", e.target.value)}
                  placeholder="Describe institution type"
                />
              </Field>
            )}
            <Field label="Year Established">
              <Input
                type="number"
                value={formData.year_established}
                onChange={e => handleChange("year_established", e.target.value)}
                placeholder="e.g. 1985"
                min="1800" max={new Date().getFullYear()}
              />
            </Field>
            <Field label="Official Website">
              <Input
                value={formData.website_url}
                onChange={e => handleChange("website_url", e.target.value)}
                placeholder="https://university.edu"
              />
            </Field>
          </div>
        )}
      </Card>

      {/* Location */}
      <Card>
        <SectionHeader icon={Globe} title="Location" isOpen={openSections.location} onToggle={() => toggleSection("location")} />
        {openSections.location && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "repeat(3, minmax(240px,1fr))", gap: 16 }}>
            <Field label="Country" required>
              <Select value={formData.country} onChange={e => handleChange("country", e.target.value)}>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            {formData.country === "India" ? (
              <Field label="State" required>
                <Select value={formData.state} onChange={e => handleChange("state", e.target.value)}>
                  <option value="">Select state / UT</option>
                  {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </Select>
              </Field>
            ) : (
              <Field label="State / Province">
                <Input value={formData.state} onChange={e => handleChange("state", e.target.value)} placeholder="State or Province" />
              </Field>
            )}
            <Field label="City" required>
              <Input value={formData.city} onChange={e => handleChange("city", e.target.value)} placeholder="City" />
            </Field>
          </div>
        )}
      </Card>

      {/* Institution Overview */}
      <Card>
        <SectionHeader icon={BookOpen} title="Institution Overview" isOpen={openSections.overview} onToggle={() => toggleSection("overview")} />
        {openSections.overview && (
          <div style={{ padding: 24 }}>
            <Field label="About the University" hint="Brief description of the university and its academic strengths — up to 300 words.">
              <Textarea
                value={formData.institution_overview}
                onChange={e => handleChange("institution_overview", e.target.value)}
                maxLength={300}
                placeholder="Describe the university's mission, academic strengths, research focus, notable programs, and what makes it stand out…"
                style={{ minHeight: 120 }}
              />
            </Field>
          </div>
        )}
      </Card>
    </div>
  );

  // ── Step 1: Contact Details ────────────────────────────────────────────────
  const renderStep1 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      {/* Primary Contact */}
      <Card>
        <SectionHeader icon={Users} title="Primary Contact Details" isOpen={openSections.primary} onToggle={() => toggleSection("primary")} />
        {openSections.primary && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "repeat(3, minmax(240px,1fr))", gap: 16 }}>
            <Field label="Name of Contact Person" required>
              <Input
                value={formData.contact_name}
                onChange={e => handleChange("contact_name", e.target.value)}
                placeholder="Full name"
              />
            </Field>
            <Field label="Designation" required>
              <Input
                value={formData.contact_designation}
                onChange={e => handleChange("contact_designation", e.target.value)}
                placeholder="e.g. Director of Admissions"
              />
            </Field>
            <Field label="Department / Office" required>
              <Input
                value={formData.contact_department}
                onChange={e => handleChange("contact_department", e.target.value)}
                placeholder="e.g. International Relations"
              />
            </Field>
            <Field label="Email Address" required>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={e => handleChange("contact_email", e.target.value)}
                placeholder="contact@university.edu"
              />
            </Field>
            <Field label="Mobile Number" required>
              <Input
                value={formData.contact_mobile}
                onChange={e => handleChange("contact_mobile", e.target.value)}
                placeholder="+91-XXXXXXXXXX"
              />
            </Field>
            <Field label="Alternate Contact Number">
              <Input
                value={formData.alternate_contact_number}
                onChange={e => handleChange("alternate_contact_number", e.target.value)}
                placeholder="+91-XXXXXXXXXX"
              />
            </Field>
          </div>
        )}
      </Card>

      {/* Representatives */}
      <Card>
        <SectionHeader
          icon={UserPlus}
          title="Additional Representatives"
          badge={representatives.length}
          isOpen={openSections.reps}
          onToggle={() => toggleSection("reps")}
        />
        {openSections.reps && (
          <div style={{ padding: 24 }}>
            <RepsTable
              reps={representatives}
              onAdd={addRep}
              onRemove={removeRep}
              onUpdate={updateRep}
              title="Representatives"
            />
          </div>
        )}
      </Card>
    </div>
  );

  // ── Step 2: Media & Branding ───────────────────────────────────────────────
  const renderStep2 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      {/* Logo */}
      <Card>
        <SectionHeader icon={Image} title="University Logo" isOpen={openSections.logo} onToggle={() => toggleSection("logo")} />
        {openSections.logo && (
          <div style={{ padding: 20 }}>
            <input ref={logoRef} type="file" accept={LOGO_CONFIG.accept} style={{ display: "none" }} onChange={handleLogoChange} />
            <SingleFileRow
              config={LOGO_CONFIG}
              uploaded={logoFile}
              isUploading={!!uploadingKeys.logo}
              onChoose={() => logoRef.current?.click()}
              onClear={() => { setLogoFile(null); handleChange("university_logo", ""); }}
            />
          </div>
        )}
      </Card>

      {/* Promo Video */}
      <Card>
        <SectionHeader icon={Video} title="Promotional Video" isOpen={openSections.video} onToggle={() => toggleSection("video")} />
        {openSections.video && (
          <div style={{ padding: 20 }}>
            <Field label="YouTube / Vimeo URL" hint="Optional — link to a campus tour, introduction, or highlights video">
              <div style={{ display: "flex" }}>
                <div style={{
                  background: T.light, border: `1.5px solid ${T.border}`, borderRight: "none",
                  borderRadius: "8px 0 0 8px", padding: "9px 12px",
                  display: "flex", alignItems: "center",
                }}>
                  <Video size={14} color={T.muted} />
                </div>
                <Input
                  value={formData.promo_video_url}
                  onChange={e => handleChange("promo_video_url", e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  style={{ borderRadius: "0 8px 8px 0" }}
                />
              </div>
            </Field>
          </div>
        )}
      </Card>

      {/* Social Media */}
      <Card>
        <SectionHeader icon={Globe} title="Social Media & Branding" isOpen={openSections.social} onToggle={() => toggleSection("social")} />
        {openSections.social && (
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              { key: "facebook_page",    label: "Facebook Page",   icon: Link,  placeholder: "https://facebook.com/university" },
              { key: "instagram_handle", label: "Instagram Handle", icon: Globe, placeholder: "@university" },
              { key: "linkedin_page",    label: "LinkedIn Page",    icon: Link,  placeholder: "https://linkedin.com/school/..." },
              { key: "youtube_channel",  label: "YouTube Channel",  icon: Video, placeholder: "https://youtube.com/@university" },
            ].map(s => (
              <Field key={s.key} label={s.label}>
                <div style={{ display: "flex" }}>
                  <div style={{
                    background: T.light, border: `1.5px solid ${T.border}`, borderRight: "none",
                    borderRadius: "8px 0 0 8px", padding: "9px 12px",
                    display: "flex", alignItems: "center",
                  }}>
                    <s.icon size={14} color={T.muted} />
                  </div>
                  <Input
                    value={formData[s.key]}
                    onChange={e => handleChange(s.key, e.target.value)}
                    placeholder={s.placeholder}
                    style={{ borderRadius: "0 8px 8px 0" }}
                  />
                </div>
              </Field>
            ))}
          </div>
        )}
      </Card>
    </div>
  );

  // ── Step 3: Academic & Participation Details ───────────────────────────────
  const renderStep3 = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      {/* Programs */}
      <Card>
        <SectionHeader icon={BookOpen} title="Programs / Disciplines Featured" isOpen={openSections.programs} onToggle={() => toggleSection("programs")} />
        {openSections.programs && (
          <div style={{ padding: 24 }}>
            <Field label="Select all that apply" required hint="Choose the disciplines your university will feature at this event.">
              <ProgramToggle
                value={formData.programs_featured}
                onChange={v => handleChange("programs_featured", v)}
              />
            </Field>
          </div>
        )}
      </Card>

      {/* Participation */}
      <Card>
        <SectionHeader icon={Award} title="Participation Details" isOpen={openSections.participation} onToggle={() => toggleSection("participation")} />
        {openSections.participation && (
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Scholarships */}
            <div style={{
              display: "flex", alignItems: "flex-start", justifyContent: "space-between",
              padding: "14px 18px", borderRadius: 10,
              background: formData.scholarships_discussed === "Yes" ? "rgba(249,115,22,.05)" : T.bg,
              border: `1.5px solid ${formData.scholarships_discussed === "Yes" ? "rgba(249,115,22,.25)" : T.border}`,
            }}>
              <div style={{ flex: 1, paddingRight: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 2 }}>
                  Will scholarships or financial aid opportunities be discussed?
                </div>
                <div style={{ fontSize: 11, color: T.muted }}>
                  Indicate if your representatives will present scholarship or funding options
                </div>
              </div>
              <YesNo value={formData.scholarships_discussed} onChange={v => handleChange("scholarships_discussed", v)} />
            </div>

            {/* One-on-one */}
            <div style={{
              display: "flex", alignItems: "flex-start", justifyContent: "space-between",
              padding: "14px 18px", borderRadius: 10,
              background: formData.one_on_one_available === "Yes" ? "rgba(249,115,22,.05)" : T.bg,
              border: `1.5px solid ${formData.one_on_one_available === "Yes" ? "rgba(249,115,22,.25)" : T.border}`,
            }}>
              <div style={{ flex: 1, paddingRight: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 2 }}>
                  Will university representatives be available for one-on-one interaction?
                </div>
                <div style={{ fontSize: 11, color: T.muted }}>
                  Individual sessions with prospective students or delegates
                </div>
              </div>
              <YesNo value={formData.one_on_one_available} onChange={v => handleChange("one_on_one_available", v)} />
            </div>
          </div>
        )}
      </Card>
    </div>
  );

  // ── Step 4: Declaration ────────────────────────────────────────────────────
  const renderStep4 = () => (
    <Card>
      <SectionHeader icon={Check} title="Declaration & Consent" isOpen={openSections.declaration} onToggle={() => toggleSection("declaration")} />
      {openSections.declaration && (
        <div style={{ padding: 24 }}>
          <div style={{
            background: "rgba(249,115,22,.06)", borderRadius: 10,
            border: `1px solid rgba(249,115,22,.2)`, padding: "16px 20px",
            marginBottom: 24,
          }}>
            <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.7, fontFamily: FONT }}>
              Please review each statement carefully. Both consents are required, along with the authorized signatory details, to complete registration.
            </p>
          </div>

          <Checkbox
            checked={formData.consent_accuracy}
            onChange={v => handleChange("consent_accuracy", v)}
            required
            label="We confirm that all information submitted is accurate and authorized by our institution."
          />
          <Checkbox
            checked={formData.consent_media}
            onChange={v => handleChange("consent_media", v)}
            required
            label="We authorize the platform to publish the submitted event details, logo, and media assets for promotional purposes."
          />

          <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(3, minmax(200px,1fr))", gap: 16 }}>
            <Field label="Authorized Signatory Name" required>
              <Input
                value={formData.authorized_signatory_name}
                onChange={e => handleChange("authorized_signatory_name", e.target.value)}
                placeholder="Full name"
              />
            </Field>
            <Field label="Designation" required>
              <Input
                value={formData.authorized_signatory_designation}
                onChange={e => handleChange("authorized_signatory_designation", e.target.value)}
                placeholder="e.g. Registrar / Vice Chancellor"
              />
            </Field>
            <Field label="Date" required>
              <Input
                type="date"
                value={formData.declaration_date}
                onChange={e => handleChange("declaration_date", e.target.value)}
              />
            </Field>
          </div>

          {!consentDone && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "rgba(220,38,38,.07)", borderRadius: 8,
              padding: "10px 14px", marginTop: 16,
            }}>
              <AlertCircle size={15} color={T.danger} />
              <span style={{ fontSize: 12, color: T.danger, fontWeight: 600, fontFamily: FONT }}>
                Both declarations and all signatory details must be provided to submit registration.
              </span>
            </div>
          )}
          {consentDone && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "rgba(22,163,74,.1)", borderRadius: 8,
              padding: "10px 14px", marginTop: 16,
            }}>
              <CheckCircle size={15} color={T.success} />
              <span style={{ fontSize: 12, color: T.success, fontWeight: 700, fontFamily: FONT }}>
                All declarations accepted. Ready to submit.
              </span>
            </div>
          )}
        </div>
      )}
    </Card>
  );

  // ── Preview Modal ──────────────────────────────────────────────────────────
  const PreviewModal = () => (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(6,21,53,.55)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
    }}>
      <div style={{
        background: T.card, borderRadius: 16, maxWidth: 640,
        width: "100%", maxHeight: "85vh", overflow: "auto",
        boxShadow: "0 24px 60px rgba(6,21,53,.3)", fontFamily: FONT,
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})`,
          padding: "18px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0,
        }}>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>Submission Preview</span>
          <button onClick={() => setPreviewMode(false)} style={{
            background: "rgba(255,255,255,.15)", border: "none", borderRadius: 6,
            padding: "4px 12px", color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 12,
          }}>Close</button>
        </div>
        <div style={{ padding: 24 }}>
          {[
            ["University Name",        formData.university_name],
            ["Institution Type",       formData.institution_type === "Other" ? formData.other_institution_type : formData.institution_type],
            ["Country",                formData.country],
            ["City / State",           [formData.city, formData.state].filter(Boolean).join(", ")],
            ["Year Established",       formData.year_established || "—"],
            ["Website",                formData.website_url || "—"],
            ["Primary Contact",        formData.contact_name],
            ["Contact Email",          formData.contact_email],
            ["Contact Mobile",         formData.contact_mobile],
            ["Representatives",        `${representatives.length} added`],
            ["University Logo",        formData.university_logo || "Not uploaded"],
            ["Promo Video",            formData.promo_video_url || "—"],
            ["Programs Featured",      formData.programs_featured.join(", ") || "—"],
            ["Scholarships Discussed", formData.scholarships_discussed || "—"],
            ["One-on-One Available",   formData.one_on_one_available || "—"],
            ["Authorized Signatory",   formData.authorized_signatory_name],
            ["Declaration Date",       formData.declaration_date],
            ["Consents Given",         consentDone ? "Yes" : "No"],
          ].map(([k, v]) => (
            <div key={k} style={{
              display: "flex", padding: "9px 0",
              borderBottom: `1px solid ${T.light}`,
            }}>
              <span style={{ width: 180, fontSize: 12, color: T.muted, fontWeight: 700, flexShrink: 0 }}>{k}</span>
              <span style={{ fontSize: 13, color: T.text, flex: 1 }}>{v || "—"}</span>
            </div>
          ))}
          <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setPreviewMode(false)}>Go Back & Edit</Btn>
            <Btn
              variant="primary" icon={Check}
              onClick={() => { setPreviewMode(false); handleSave("submitted"); }}
              disabled={!consentDone || saving}
            >
              {saving ? "Submitting…" : "Confirm & Submit"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Full render ────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: FONT }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
      `}</style>

      {previewMode && <PreviewModal />}

      <h2 style={{ color: T.navy, fontSize: "18px", margin: 0, padding: "16px 0 0", textAlign: "center", fontFamily: FONT }}>
        UNIVERSITY REGISTRATION
      </h2>

      <div style={{ width: "100%", padding: "24px 24px 48px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 24, alignItems: "start" }}>

          {/* ── Form area ── */}
          <div>
            <StepBar current={step} />

            <div style={{ marginTop: 18 }}>
              {step === 0 && renderStep0()}
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
            </div>

            {/* Navigation */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 24, paddingBottom: 8,
            }}>
              <div>
                {step > 0 && (
                  <Btn variant="outline" onClick={() => setStep(s => s - 1)}>← Back</Btn>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {saveStatus === "saved" && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.success, fontWeight: 700, fontSize: 13, fontFamily: FONT }}>
                    <CheckCircle size={15} /> Saved
                  </span>
                )}
                {saveStatus === "error" && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.danger, fontWeight: 700, fontSize: 13, fontFamily: FONT }}>
                    <AlertCircle size={15} /> Save failed
                  </span>
                )}
                <Btn variant="secondary" icon={Save} onClick={() => handleSave("draft")} disabled={saving}>
                  Save Draft
                </Btn>
                {step < STEPS.length - 1 ? (
                  <Btn variant="primary" disabled={!validateStep(step)} onClick={() => setStep(s => s + 1)}>
                    Next →
                  </Btn>
                ) : (
                  <>
                    <Btn variant="navy" icon={Eye} onClick={() => setPreviewMode(true)}>
                      Preview
                    </Btn>
                    <Btn
                      variant="success"
                      icon={Check}
                      onClick={() => handleSave("submitted")}
                      disabled={saving || !consentDone}
                    >
                      {saving ? "Submitting…" : existingProfile ? "Update Registration" : "Submit Registration"}
                    </Btn>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── Sidebar ── */}
          <div style={{ position: "sticky", top: 24 }}>
            <Card style={{ padding: 20, marginBottom: 18 }}>
              <div style={{
                fontWeight: 800, fontSize: 13, color: T.navy, marginBottom: 14,
                textTransform: "uppercase", letterSpacing: .5, fontFamily: FONT,
              }}>
                Profile Completion
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 14 }}>
                <DonutChart pct={completion} />
              </div>
              <ProgressBar value={completion} color={completion > 80 ? T.success : T.orange} />

              {missingRequired.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8, fontFamily: FONT }}>
                    Required Fields Missing
                  </div>
                  {missingRequired.map(f => (
                    <div key={f} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.danger, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: T.text, fontFamily: FONT }}>
                        {f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {missingRequired.length === 0 && (
                <div style={{
                  marginTop: 14, display: "flex", alignItems: "center", gap: 6,
                  background: "rgba(22,163,74,.1)", borderRadius: 8, padding: "8px 12px",
                }}>
                  <CheckCircle size={14} color={T.success} />
                  <span style={{ fontSize: 12, color: T.success, fontWeight: 700, fontFamily: FONT }}>
                    All required fields filled
                  </span>
                </div>
              )}

              {/* Status */}
              <div style={{ marginTop: 18, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8, fontFamily: FONT }}>
                  Status
                </div>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: existingProfile ? "rgba(249,115,22,.1)" : T.light,
                  borderRadius: 8, padding: "6px 12px",
                }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: existingProfile ? T.orange : T.muted }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: existingProfile ? T.orange : T.muted, fontFamily: FONT }}>
                    {existingProfile ? "Profile Exists" : "Not Yet Saved"}
                  </span>
                </div>
              </div>

              {/* Representatives count */}
              <div style={{ marginTop: 14, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8, fontFamily: FONT }}>
                  Representatives
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: representatives.length > 0 ? T.success : T.border }} />
                  <span style={{ fontSize: 12, color: T.text, fontFamily: FONT }}>
                    {representatives.filter(r => r.name).length} of {representatives.length} filled
                  </span>
                </div>
              </div>

              {/* Declaration */}
              <div style={{ marginTop: 14, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8, fontFamily: FONT }}>
                  Declaration
                </div>
                {[
                  ["Accuracy", formData.consent_accuracy],
                  ["Media Use", formData.consent_media],
                  ["Signatory", !!formData.authorized_signatory_name],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: val ? T.success : T.border, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: val ? T.success : T.muted, fontWeight: val ? 700 : 400, fontFamily: FONT }}>{label}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Step guide */}
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, marginBottom: 10, fontFamily: FONT }}>
                Steps
              </div>
              {STEPS.map((s, i) => (
                <div
                  key={i}
                  onClick={() => { if (i <= step || validateStep(step)) setStep(i); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", borderRadius: 8, marginBottom: 4,
                    cursor: "pointer",
                    background: step === i ? "rgba(249,115,22,.1)" : "transparent",
                    border: `1px solid ${step === i ? "rgba(249,115,22,.3)" : "transparent"}`,
                    transition: "all .15s",
                  }}
                >
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                    background: i < step ? T.success : step === i ? T.orange : T.light,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {i < step
                      ? <Check size={11} color="#fff" strokeWidth={3} />
                      : <s.icon size={11} color={step === i ? "#fff" : T.muted} />}
                  </div>
                  <span style={{
                    fontSize: 12, fontWeight: step === i ? 700 : 500,
                    color: step === i ? T.orange : i < step ? T.success : T.muted,
                    fontFamily: FONT,
                  }}>{s.label}</span>
                </div>
              ))}
            </Card>
          </div>

        </div>
      </div>
    </div>
  );
}

export default UniversityRegistration;